/** A palette is only useful when it survives actual slide jobs. Five exact
 * 16:9 situations, bounded copy, and the same colors the user can export. */

import { useMemo, useState } from "react";

import type { PaletteSystem, PreviewCopy } from "@core/index.js";

import { selectWorkingPalette, useStudio } from "../app/store";
import { DEFAULT_SLIDE_PALETTE, SlidePreview, type SlidePalette, type SlideVariant } from "../components/SlidePreview";
import { domRigMeasurer, fitLabel, type RigMeasurer, type RigStacks } from "../measure/fit";
import { previewTemplateList } from "../wiring/catalog";
import { previewCopyFor } from "../wiring/engines";
import type { PreviewTemplate } from "../wiring/types";

const TABS: Record<PreviewTemplate["id"], { label: string; variant: SlideVariant }> = {
  cover: { label: "Opening", variant: "statement" },
  title_body: { label: "Argument", variant: "titleBody" },
  quote: { label: "Quote", variant: "quote" },
  image_text: { label: "Image + words", variant: "imageText" },
  evidence_data: { label: "Number", variant: "metric" },
};

const COPY_PRESETS = [
  { id: "decision", label: "The decision", headline: "Make the next decision easier.", body: "Put the argument where people can see it. Give the evidence room. Let the appendix keep the furniture." },
  { id: "launch", label: "The launch", headline: "The useful thing ships Tuesday.", body: "Three teams, one release, and no ceremonial fog machine. Here is what changes for customers." },
  { id: "proof", label: "The proof", headline: "The boring metric did something exciting.", body: "Time to first contribution fell without moving the risk elsewhere. The next page shows the work." },
] as const;

const LIMITS = { headline: 72, body: 190 } as const;
const SYSTEM_STACKS: RigStacks = {
  display: 'Georgia, "Times New Roman", serif',
  body: 'Inter, ui-sans-serif, system-ui, sans-serif',
  utility: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const slidePalette = (palette: PaletteSystem | null): SlidePalette => palette ? {
  background: palette.roles.background?.hex ?? DEFAULT_SLIDE_PALETTE.background,
  surface: palette.roles.surface?.hex ?? DEFAULT_SLIDE_PALETTE.surface,
  text: palette.roles.text?.hex ?? DEFAULT_SLIDE_PALETTE.text,
  muted: palette.roles.muted_text?.hex ?? DEFAULT_SLIDE_PALETTE.muted,
  accent: palette.roles.accent_primary?.hex ?? DEFAULT_SLIDE_PALETTE.accent,
  accentSecondary: palette.roles.accent_secondary?.hex,
  onAccent: palette.roles.on_accent?.hex,
} : DEFAULT_SLIDE_PALETTE;

export const PreviewLab = ({ measurer = domRigMeasurer }: { measurer?: RigMeasurer }) => {
  const { state, dispatch } = useStudio();
  const [templateId, setTemplateId] = useState<PreviewTemplate["id"]>("cover");
  const palettes = state.colourRun ? [...state.colourRun.systems, ...state.colourRun.companionSystems] : [];
  const palette = selectWorkingPalette(state) ?? palettes.find((item) => item.id === state.selectedPaletteId) ?? null;
  const { copy: words, fixture } = previewCopyFor(state.brief);
  const templateIndex = Math.max(0, previewTemplateList.findIndex((item) => item.id === templateId));
  const template = previewTemplateList[templateIndex] ?? previewTemplateList[0];
  if (!template) return null;

  const fit = useMemo(() => measurer({
    kicker: "YOUR DECK",
    headline: words.slideTitle ?? words.deckTitle ?? "Make the point visible.",
    subheadline: words.subtitle ?? "",
    body: words.body ?? "The argument gets a shape. The evidence gets room. The audience gets the point.",
    quote: words.quote ?? "Clarity is a form of respect.",
    caption: words.attribution ?? "Project principle",
    dataLabel: words.metricLabel ?? "LESS TIME TO FIRST CONTRIBUTION",
    dataValue: words.metric ?? "48%",
  }, template, SYSTEM_STACKS), [measurer, template, words]);
  const tab = TABS[template.id];

  const move = (delta: number) => {
    const next = (templateIndex + delta + previewTemplateList.length) % previewTemplateList.length;
    const item = previewTemplateList[next];
    if (item) setTemplateId(item.id);
  };

  return (
    <div className="preview-lab color-preview-lab">
      <p className="eyebrow">The slide test</p>
      <h2 className="step-title">Pretty swatches are cheap. Does the color do the work?</h2>
      <p className="step-intro">Five common slide jobs. Exact 16:9. Same palette. No tiny decorative rectangles pretending to be a presentation.</p>

      <div className="specimen-presets" aria-label="Sample copy presets">
        <span>Give it some words:</span>
        {COPY_PRESETS.map((preset) => (
          <button type="button" key={preset.id} onClick={() => dispatch({ type: "setPreviewCopy", previewCopy: { ...state.brief.previewCopy, slideTitle: preset.headline, body: preset.body } })}>{preset.label}</button>
        ))}
      </div>

      <div className="slide-tabs" role="tablist" aria-label="Slide types">
        {previewTemplateList.map((item, index) => <button key={item.id} type="button" role="tab" aria-selected={item.id === template.id} onClick={() => setTemplateId(item.id)}><span>{String(index + 1).padStart(2, "0")}</span>{TABS[item.id].label}</button>)}
      </div>

      <div className="lab-layout">
        <div className="slide-stage">
          <SlidePreview
            title={words.slideTitle ?? words.deckTitle ?? "Make the point visible."}
            body={words.body ?? "The argument gets a shape. The evidence gets room. The audience gets the point."}
            kicker={words.deckTitle && words.deckTitle !== words.slideTitle ? words.deckTitle : "YOUR DECK"}
            quote={words.quote}
            attribution={words.attribution}
            metric={words.metric}
            metricLabel={words.metricLabel}
            palette={slidePalette(palette)}
            fonts={SYSTEM_STACKS}
            variant={tab.variant}
            label={`${tab.label} slide · exact 16:9`}
          />
          <div className="slide-stage__nav">
            <button type="button" className="round-action" onClick={() => move(-1)} aria-label="Previous slide job">←</button>
            <span>{String(templateIndex + 1).padStart(2, "0")} / {String(previewTemplateList.length).padStart(2, "0")}</span>
            <button type="button" className="round-action" onClick={() => move(1)} aria-label="Next slide job">→</button>
          </div>
          <div className={`fit-verdict fit-verdict--${fit.status}`}><strong>{fitLabel(fit.status)}</strong><span>{fit.status === "does_not_fit" ? "The words need an edit before this layout is safe." : "The words and roles survive this layout."}</span></div>
        </div>

        <aside className="lab-notes">
          <section className="palette-switcher">
            <p className="eyebrow">CHANGE THE LIGHT</p>
            <h3>{palette?.name ?? "Your palette"}</h3>
            <div className="palette-options palette-options--rich">
              {palettes.map((item) => (
                <button key={item.id} type="button" aria-pressed={item.id === state.selectedPaletteId} onClick={() => dispatch({ type: "selectPalette", systemId: item.id })}>
                  <span className="palette-mini">{Object.values(item.roles).slice(0, 5).map((role) => <i key={role.role} style={{ background: role.hex }} />)}</span>
                  <strong>{item.name}</strong><small>{item.strategy.replaceAll("_", " ")}</small>
                </button>
              ))}
            </div>
          </section>

          <details className="disclosure specimen-editor">
            <summary>Try your own words</summary>
            <div className="disclosure-body">
              <p>Short limits protect the slide. Your copy stays in this tab.</p>
              <label><span>Headline</span><textarea maxLength={LIMITS.headline} value={words.slideTitle ?? ""} onChange={(event) => dispatch({ type: "setPreviewCopy", previewCopy: { ...state.brief.previewCopy, slideTitle: event.target.value } as PreviewCopy })} /><small>{(words.slideTitle ?? "").length}/{LIMITS.headline}</small></label>
              <label><span>Supporting copy</span><textarea maxLength={LIMITS.body} value={words.body ?? ""} onChange={(event) => dispatch({ type: "setPreviewCopy", previewCopy: { ...state.brief.previewCopy, body: event.target.value } as PreviewCopy })} /><small>{(words.body ?? "").length}/{LIMITS.body}</small></label>
            </div>
          </details>
          {fixture ? <p className="truth-note">Sample words are filling the remaining fields. Replace only what helps; this is a color test, not homework.</p> : null}
        </aside>
      </div>
    </div>
  );
};
